import { GeckoMeasurement } from 'types'

/**
 * FFS Report Types
 *
 * Types specific to the FFS (Fitness-For-Service) report for RAUT pipe indications.
 */

// Region selection for an indication
export interface IndicationSelection {
  x: {
    min: GeckoMeasurement
    max: GeckoMeasurement
  }
  y: {
    min: GeckoMeasurement
    max: GeckoMeasurement
  }
}

// Simplified indication data for the report
export interface FfsIndicationData {
  id: string
  title: string
  selection: IndicationSelection | null
  componentId: string
}

// FFS LTA Report outcome (from ffs-lta-report entry)
export interface FfsLtaReportOutcome {
  result: 'pass' | 'derate' | 'fail' | 'pending' | 'skipped'
  recommendation: string
  inspector: string
  scenario: string
}

// ==================== Full FFS LTA Report Data ====================

/** Measurement type as stored in JSON (used for serialization) */
export interface MeasurementJson {
  num: number
  unit: string
}

/** User-provided parameters */
export interface FfsUserParams {
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  selectedMawpCalcThickness: 't_mm' | 't_am' | 't_c' | null
}

/** Data values from measurements and calculations */
export interface FfsDataValues {
  tmm: GeckoMeasurement | null
  trd: GeckoMeasurement | null
  tc: GeckoMeasurement | null
  longitudinalFlawLength: GeckoMeasurement | null
  latitudinalFlawLength: GeckoMeasurement | null
  innerDiameter: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  lambda: GeckoMeasurement | null
  nominal: GeckoMeasurement | null
  t_ml: GeckoMeasurement | null
  d_ml: GeckoMeasurement | null
  corrosionOnInnerSurface: boolean
}

/** MAWP calculation values */
export interface FfsMawpValues {
  e: GeckoMeasurement | null
  y: GeckoMeasurement | null
  w: GeckoMeasurement | null
  s_y: GeckoMeasurement | null
  tsl: GeckoMeasurement | null
  mawp_c: GeckoMeasurement | null
  mawp_l: GeckoMeasurement | null
  mawp_design: GeckoMeasurement | null
  mawpr: GeckoMeasurement | null
  calcType: 'thinWall' | 'thickWall' | null
}

/** Part 4 specific: Calculated values */
export interface FfsPartFourCalculatedValues {
  rt: GeckoMeasurement | null
  rsf_a: GeckoMeasurement | null
  q: GeckoMeasurement | null
  l: GeckoMeasurement | null
  ts_am: GeckoMeasurement | null
  tc_am: GeckoMeasurement | null
}

/** Part 4 specific: Thickness criteria */
export interface FfsPartFourThicknessCriteria {
  t_lim: GeckoMeasurement | null
  t_c_min: GeckoMeasurement | null
  t_l_min: GeckoMeasurement | null
  thickness_criteria_min: GeckoMeasurement | null
  effective_measured_thickness: GeckoMeasurement | null
}

/** Part 5 specific: Flaw size criteria */
export interface FfsPartFiveFlawSizeCriteria {
  rt: GeckoMeasurement | null
  tmm_threshold: GeckoMeasurement | null
  lmsd: GeckoMeasurement | null
  lmsd_threshold: GeckoMeasurement | null
  remainingThicknessThresholdRatio: GeckoMeasurement | null
}

/** Part 5 specific: Final acceptability criteria */
export interface FfsPartFiveFinalAcceptability {
  rsf: GeckoMeasurement | null
  rsf_a: GeckoMeasurement | null
  mt: GeckoMeasurement | null
  rt_threshold: GeckoMeasurement | null
}

/** Base FFS LTA Report data (common to all parts) */
export interface FfsLtaReportDataBase {
  id: string
  outcome: FfsLtaReportOutcome
  ffsLevel: '1' | '2'
  ffsPart: 'part-4' | 'part-5'
  isDefault: boolean
  userParams: FfsUserParams
  dataValues: FfsDataValues
  mawp: FfsMawpValues
}

/** Part 4 specific FFS LTA Report */
export interface FfsLtaReportDataPartFour extends FfsLtaReportDataBase {
  ffsPart: 'part-4'
  calculatedValues: FfsPartFourCalculatedValues
  thicknessCriteria: FfsPartFourThicknessCriteria
}

/** Part 5 specific FFS LTA Report */
export interface FfsLtaReportDataPartFive extends FfsLtaReportDataBase {
  ffsPart: 'part-5'
  flawSizeCriteria: FfsPartFiveFlawSizeCriteria
  finalAcceptabilityCriteria: FfsPartFiveFinalAcceptability
}

/** Full FFS LTA Report data (union of Part 4 and Part 5) */
export type FfsLtaReportData =
  | FfsLtaReportDataPartFour
  | FfsLtaReportDataPartFive

// ==================== Legacy simplified type (kept for backwards compatibility) ====================

/** Simplified FFS LTA Report data (legacy - use FfsLtaReportData for full data) */
export interface FfsLtaReportDataSimple {
  id: string
  outcome: FfsLtaReportOutcome
  ffsLevel: '1' | '2'
  ffsPart: 'part-4' | 'part-5'
  isDefault: boolean
}

// Combined indication with its FFS report
// Note: ffsReport is guaranteed to be non-null because indications without
// an ffs-lta-report are filtered out in useIndicationsWithFfsReports
export interface IndicationWithFfsReport {
  indication: FfsIndicationData
  ffsReport: FfsLtaReportData
}

// ==================== Type Guards ====================

/** Type guard for Part 4 reports */
export function isPartFourReport(
  report: FfsLtaReportData
): report is FfsLtaReportDataPartFour {
  return report.ffsPart === 'part-4'
}

/** Type guard for Part 5 reports */
export function isPartFiveReport(
  report: FfsLtaReportData
): report is FfsLtaReportDataPartFive {
  return report.ffsPart === 'part-5'
}
